import { useState, useCallback, useRef, useEffect } from 'react';
import type { VoiceInputState } from '../types';

// Augment Window for SpeechRecognition
interface SpeechRecognitionEvent extends Event {
  results: SpeechRecognitionResultList;
  resultIndex: number;
}

interface SpeechRecognitionInstance extends EventTarget {
  lang: string;
  continuous: boolean;
  interimResults: boolean;
  start(): void;
  stop(): void;
  abort(): void;
  onresult: ((event: SpeechRecognitionEvent) => void) | null;
  onerror: ((event: Event & { error: string }) => void) | null;
  onend: (() => void) | null;
  onspeechend: (() => void) | null;
}

type SpeechRecognitionConstructor = new () => SpeechRecognitionInstance;

function getSpeechRecognition(): SpeechRecognitionConstructor | null {
  const w = window as unknown as Record<string, unknown>;
  return (w.SpeechRecognition ?? w.webkitSpeechRecognition ?? null) as SpeechRecognitionConstructor | null;
}

export interface UseVoiceInputReturn {
  /** Whether Web Speech API is supported */
  isSupported: boolean;
  /** Current state of voice input */
  state: VoiceInputState;
  /** Last recognized transcript */
  transcript: string;
  /** Start listening */
  start: () => void;
  /** Stop listening */
  stop: () => void;
  /** Reset state and transcript */
  reset: () => void;
  /** Last SpeechRecognition error type (null when no error) */
  errorType: string | null;
}

export interface UseVoiceInputOptions {
  /** BCP-47 language tag (default: browser language) */
  lang?: string;
}

/**
 * Hook wrapping Web Speech API (SpeechRecognition) for voice input.
 * Returns isSupported=false if the API is not available.
 */
export function useVoiceInput(options?: UseVoiceInputOptions): UseVoiceInputReturn {
  const SpeechRecognitionCtor = typeof window !== 'undefined' ? getSpeechRecognition() : null;
  const hasMediaDevices = typeof navigator !== 'undefined' && !!navigator.mediaDevices?.getUserMedia;
  const isSupported = SpeechRecognitionCtor != null && hasMediaDevices;

  const [state, setState] = useState<VoiceInputState>('idle');
  const [transcript, setTranscript] = useState('');
  const [errorType, setErrorType] = useState<string | null>(null);
  const recognitionRef = useRef<SpeechRecognitionInstance | null>(null);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (recognitionRef.current) {
        try { recognitionRef.current.abort(); } catch { /* ignore */ }
        recognitionRef.current = null;
      }
    };
  }, []);

  const start = useCallback(() => {
    if (!SpeechRecognitionCtor) return;
    if (recognitionRef.current) {
      try { recognitionRef.current.abort(); } catch { /* ignore */ }
    }

    const recognition = new SpeechRecognitionCtor();
    recognition.lang = options?.lang ?? navigator.language;
    recognition.continuous = false;
    recognition.interimResults = false;
    recognitionRef.current = recognition;

    recognition.onresult = (event: SpeechRecognitionEvent) => {
      const result = event.results[event.resultIndex];
      if (result?.[0]) {
        setTranscript(result[0].transcript);
        setState('processing');
      }
    };

    recognition.onerror = (event: Event & { error: string }) => {
      const errType = event.error;
      setErrorType(errType);
      setState('error');
      recognitionRef.current = null;
      // Auto-reset after 3 seconds
      setTimeout(() => {
        setState((s) => (s === 'error' ? 'idle' : s));
        setErrorType(null);
      }, 3000);
    };

    recognition.onend = () => {
      // If we were still listening (not processing/error), go to idle
      setState((s) => (s === 'listening' ? 'idle' : s));
      recognitionRef.current = null;
    };

    recognition.onspeechend = () => {
      setState((s) => (s === 'listening' ? 'processing' : s));
    };

    setErrorType(null);
    setState('listening');
    try {
      recognition.start();
    } catch {
      setState('error');
      recognitionRef.current = null;
    }
  }, [SpeechRecognitionCtor, options?.lang]);

  const stop = useCallback(() => {
    if (recognitionRef.current) {
      try { recognitionRef.current.stop(); } catch { /* ignore */ }
    }
    setState((s) => (s === 'listening' ? 'processing' : s));
  }, []);

  const reset = useCallback(() => {
    if (recognitionRef.current) {
      try { recognitionRef.current.abort(); } catch { /* ignore */ }
      recognitionRef.current = null;
    }
    setState('idle');
    setTranscript('');
    setErrorType(null);
  }, []);

  return { isSupported, state, transcript, start, stop, reset, errorType };
}
